// Geometry for the day/week time grid, derived from the user's display options:
//   • start / end hour — the visible window of the day (rows outside it are not shown)
//   • tick minutes      — spacing of the horizontal gridlines / rows ("Min interval")
//   • zoom percent      — overall vertical scale ("Hour row height")
//
// Events are always positioned on a fine 5-minute lattice so their placement stays
// exact regardless of the (coarser) tick spacing. The day height is anchored to the
// zoom so changing the tick interval re-distributes the SAME height into more/fewer
// rows instead of rescaling the day.
//
// Gutter time labels ride the fine lattice too, NOT the tick rows, so their density
// is a pure function of how much vertical room the zoom gives them - ":30" gets a
// label even when the user has set hourly gridlines.

export const CALENDAR_FINE_MINUTES = 5;

// At 100% zoom a 30-minute span is BASE_SLOT_REM tall. Everything else is derived
// from this minute-rate, so the per-minute scale is identical to the pre-config grid.
export const BASE_SLOT_REM = 3.5;
export const BASE_SLOT_MINUTES = 30;

// Root font size the rem-based grid resolves against. Only used to turn the rem
// geometry into a px budget for the label-density decision below.
const ROOT_FONT_PX = 16;

// Candidate label intervals, coarsest first. Every value divides 60 and is a
// multiple of CALENDAR_FINE_MINUTES, so a label always lands both on a real row
// of the fine lattice and on a clean minute mark.
const LABEL_INTERVAL_LADDER = [60, 30, 15, 10, 5];

// A label needs at least this much vertical room to be worth drawing - the
// line-height of the axis text (text-xs/5 = 20px).
const MIN_LABEL_PX = 20;

/** Rendered height of one minute of the day grid, in px, at a given zoom. */
export function pxPerMinuteForZoom(zoomPercent = 100) {
  return ((BASE_SLOT_REM * (zoomPercent / 100)) / BASE_SLOT_MINUTES) * ROOT_FONT_PX;
}

function clampInt(value, min, max, fallback) {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

/**
 * Derive the geometry of the day/week time grid from the display options. Returns
 * the visible window (in minutes), the fine-lattice row count, the tick rows, the
 * gutter time labels, and ready-to-use CSS `grid-template-rows` strings for the
 * background tick grid and the foreground (event-positioning) grid.
 */
export function getCalendarTimeAxis({
  startHour = 0,
  endHour = 24,
  tickMinutes = 30,
  zoomPercent = 100,
} = {}) {
  const start = clampInt(startHour, 0, 23, 0);
  let end = clampInt(endHour, 1, 24, 24);
  if (end <= start) end = Math.min(24, start + 1);
  const tick = clampInt(tickMinutes, CALENDAR_FINE_MINUTES, 240, BASE_SLOT_MINUTES);

  const startMinutes = start * 60;
  const endMinutes = end * 60;
  const totalMinutes = endMinutes - startMinutes;
  const fineCount = Math.round(totalMinutes / CALENDAR_FINE_MINUTES);

  const remPerMinute = (BASE_SLOT_REM * (zoomPercent / 100)) / BASE_SLOT_MINUTES;

  // Label density is deliberately INDEPENDENT of the tick interval: labels are
  // placed on the fine lattice, so ":30" can carry a label even when the
  // gridlines are hourly. Pick the finest ladder interval that still leaves room
  // for the text. The ladder descends, so the last one that fits is the finest.
  //
  // NOTE: tick rows are `minmax(Xrem, 1fr)` and stretch past their rem minimum
  // when the visible window is shorter than the viewport, so this px rate is a
  // FLOOR. The rule can therefore under-label, never over-label - the safe way
  // round, and it avoids having to measure the real row height.
  const pxPerMinute = remPerMinute * ROOT_FONT_PX;
  let labelMinutes = LABEL_INTERVAL_LADDER[0];
  for (const candidate of LABEL_INTERVAL_LADDER) {
    if (candidate * pxPerMinute >= MIN_LABEL_PX) labelMinutes = candidate;
  }

  // Tick rows tile the window. The final row is shortened if the interval does not
  // divide the window evenly (e.g. a 90-min tick across a 10-hour day) so the grid
  // height always matches the fine lattice exactly.
  const ticks = [];
  for (let offset = 0; offset < totalMinutes; offset += tick) {
    const minutes = Math.min(tick, totalMinutes - offset);
    const startMinute = startMinutes + offset;
    ticks.push({
      key: startMinute,
      startMinute,
      minutes,
      heightRem: remPerMinute * minutes,
      // Kept for hour-anchored behaviour (the day view's AM/PM scroll targets);
      // the time TEXT lives on the separate `labels` list below.
      isHour: startMinute % 60 === 0,
      hour: startMinute / 60,
    });
  }

  const tickRowTemplate = ticks.map((t) => `minmax(${t.heightRem}rem, 1fr)`).join(" ");
  const fineRowTemplate = `1.75rem repeat(${fineCount}, minmax(0, 1fr)) auto`;

  // Time labels for the left gutter, on the FINE lattice rather than the tick
  // rows. `labelMinutes` divides 60, so every hour boundary is always included.
  const labels = [];
  for (let minute = startMinutes; minute < endMinutes; minute += labelMinutes) {
    labels.push({
      key: minute,
      startMinute: minute,
      isHour: minute % 60 === 0,
      hour: minute / 60,
      minute: minute % 60,
      // Same 1-based fine-lattice row base as gridRowsForMinuteSpan, so a label
      // sits on exactly the row its time occupies in the events grid.
      row: 2 + Math.round((minute - startMinutes) / CALENDAR_FINE_MINUTES),
    });
  }

  return {
    startHour: start,
    endHour: end,
    tickMinutes: tick,
    startMinutes,
    endMinutes,
    totalMinutes,
    fineCount,
    ticks,
    tickRowTemplate,
    fineRowTemplate,
    labelMinutes,
    labels,
  };
}

/**
 * Grid placement (1-based rows on the fine lattice) for an arbitrary minute span
 * within the day, clamped to the visible window. Returns null when the span falls
 * entirely outside the window (so callers can skip drawing it). This is the shared
 * minute→row math used by both event cards and the day-view session rail so the two
 * line up exactly regardless of zoom / tick interval.
 *
 * @param {number} startMinutes minutes-into-the-day of the span start
 * @param {number} endMinutes   minutes-into-the-day of the span end
 * @param {ReturnType<typeof getCalendarTimeAxis>} axis
 * @returns {{ startRow: number, span: number } | null}
 */
export function gridRowsForMinuteSpan(startMinutes, endMinutes, axis) {
  const clampedStart = Math.min(Math.max(startMinutes, axis.startMinutes), axis.endMinutes);
  const clampedEnd = Math.min(Math.max(endMinutes, axis.startMinutes), axis.endMinutes);
  if (clampedEnd <= clampedStart) {
    return null;
  }
  const startBlock = Math.floor((clampedStart - axis.startMinutes) / CALENDAR_FINE_MINUTES);
  const rawSpan = Math.max(Math.ceil((clampedEnd - clampedStart) / CALENDAR_FINE_MINUTES), 1);
  const maxSpan = Math.max(axis.fineCount - startBlock, 1);
  const span = Math.min(rawSpan, maxSpan);
  return { startRow: 2 + startBlock, span };
}

/**
 * Grid placement (1-based rows on the fine lattice) for an event, clamped to the
 * visible window: events starting before the window begin at the top row, and
 * events running past the end are truncated to the last row.
 */
export function eventGridPositionForAxis(event, axis) {
  const startMinutes = event.startAt.hour * 60 + event.startAt.minute;
  const duration = Math.max(
    event.endAt.diff(event.startAt, "minutes").minutes,
    CALENDAR_FINE_MINUTES,
  );
  const endMinutes = startMinutes + duration;

  const clampedStart = Math.min(Math.max(startMinutes, axis.startMinutes), axis.endMinutes);
  const clampedEnd = Math.min(Math.max(endMinutes, axis.startMinutes), axis.endMinutes);

  const startBlock = Math.floor((clampedStart - axis.startMinutes) / CALENDAR_FINE_MINUTES);
  const rawSpan = Math.max(Math.ceil((clampedEnd - clampedStart) / CALENDAR_FINE_MINUTES), 1);
  const maxSpan = Math.max(axis.fineCount - startBlock, 1);
  const span = Math.min(rawSpan, maxSpan);
  const startRow = 2 + startBlock;
  return { startRow, span };
}

/**
 * Placement for the "current time" indicator, or null when now falls outside the
 * visible window (so callers can hide the line entirely).
 */
export function nowIndicatorForAxis(nowMinutes, axis) {
  if (nowMinutes < axis.startMinutes || nowMinutes >= axis.endMinutes) {
    return null;
  }
  const offset = nowMinutes - axis.startMinutes;
  return {
    rowStart: 2 + Math.floor(offset / CALENDAR_FINE_MINUTES),
    rowOffsetPercent: ((offset % CALENDAR_FINE_MINUTES) / CALENDAR_FINE_MINUTES) * 100,
  };
}
